"use client";

import { useCallback, useEffect, useRef } from "react";
import { io, type Socket } from "socket.io-client";

export type RealtimeOrigin = "local" | "broadcast" | "socket";

export interface RealtimeMessageMeta {
  origin: RealtimeOrigin;
  clientId?: string;
}

export interface UseRealtimeChannelOptions<
  TPayload extends { userId?: string | null },
> {
  userId?: string | null;
  enabled?: boolean;
  channelName: string;
  socketEvent: string;
  wsUrl?: string;
  onMessage?: (payload: TPayload & RealtimeMessageMeta) => void;
  filter?: (payload: TPayload) => boolean;
  mapPayload?: (payload: TPayload) => TPayload;
}

export type WsConnectionStatus =
  | "connecting"
  | "connected"
  | "disconnected"
  | "error";

export interface WsStatusEventDetail {
  status: WsConnectionStatus;
  channel: string;
  wsUrl: string;
  reason?: string;
  retryCount?: number;
}

export const WS_STATUS_EVENT = "tm:ws:status";

/**
 * 通用实时同步 Hook：封装 BroadcastChannel + Socket.IO 逻辑，供具体业务 Hook 复用。
 */
export function useRealtimeChannel<
  TPayload extends { userId?: string | null; updatedAt?: string },
>(options: UseRealtimeChannelOptions<TPayload>) {
  const {
    userId,
    enabled = true,
    channelName,
    socketEvent,
    wsUrl = process.env.NEXT_PUBLIC_WS_URL || "",
    onMessage,
    filter,
    mapPayload,
  } = options;

  const broadcastRef = useRef<BroadcastChannel | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const clientIdRef = useRef<string>("");

  if (!clientIdRef.current) {
    clientIdRef.current =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2);
  }

  const shouldAccept = useCallback(
    (payload: TPayload | null | undefined) => {
      if (!payload) return false;
      if (userId && payload.userId && payload.userId !== userId) {
        return false;
      }
      if (typeof filter === "function") {
        return filter(payload);
      }
      return true;
    },
    [filter, userId],
  );

  const emitMessage = useCallback(
    (payload: TPayload, origin: RealtimeOrigin, clientId?: string) => {
      if (!shouldAccept(payload)) return;
      const mapped = mapPayload ? mapPayload(payload) : payload;
      onMessage?.({
        ...(mapped as TPayload),
        origin,
        clientId,
      });
    },
    [mapPayload, onMessage, shouldAccept],
  );

  const emitMessageRef = useRef(emitMessage);
  useEffect(() => {
    emitMessageRef.current = emitMessage;
  }, [emitMessage]);

  useEffect(() => {
    if (!enabled || !userId) return;
    if (
      typeof window === "undefined" ||
      typeof BroadcastChannel === "undefined"
    )
      return;

    const channel = new BroadcastChannel(channelName);
    broadcastRef.current = channel;

    const handleMessage = (
      event: MessageEvent<TPayload & RealtimeMessageMeta>,
    ) => {
      const payload = event.data;
      if (!payload) return;
      if (payload.clientId && payload.clientId === clientIdRef.current) return;
      emitMessageRef.current?.(
        payload,
        payload.origin ?? "broadcast",
        payload.clientId,
      );
    };

    channel.addEventListener("message", handleMessage);

    return () => {
      channel.removeEventListener("message", handleMessage);
      channel.close();
      if (broadcastRef.current === channel) {
        broadcastRef.current = null;
      }
    };
  }, [channelName, enabled, userId]);

  useEffect(() => {
    if (!enabled || !userId) return;
    if (typeof window === "undefined") return;
    if (!process.env.NEXT_PUBLIC_WS_URL) return; // No WS server configured
    if (!wsUrl) return; // No WS URL provided

    const notifyStatus = (detail: WsStatusEventDetail) => {
      if (typeof window === "undefined") return;
      window.dispatchEvent(new CustomEvent(WS_STATUS_EVENT, { detail }));
    };

    const socket = io(wsUrl, {
      transports: ["websocket", "polling"],
      autoConnect: true,
    });
    socketRef.current = socket;
    notifyStatus({ status: "connecting", channel: channelName, wsUrl });
    let failTimer: ReturnType<typeof setTimeout> | null = setTimeout(() => {
      if (!socket.connected) {
        notifyStatus({
          status: "error",
          channel: channelName,
          wsUrl,
          reason: "timeout",
        });
      }
    }, 5000);

    const handleConnect = () => {
      socket.emit("authenticate", userId);
      if (failTimer) {
        clearTimeout(failTimer);
        failTimer = null;
      }
      notifyStatus({ status: "connected", channel: channelName, wsUrl });
    };

    const handleSocketEvent = (payload: TPayload) => {
      if (!payload) return;
      const enriched = {
        ...payload,
        origin: "socket" as const,
        clientId: clientIdRef.current,
      };
      emitMessageRef.current?.(enriched, "socket", clientIdRef.current);
      if (broadcastRef.current) {
        broadcastRef.current.postMessage(enriched);
      }
    };

    const handleDisconnect = () => {
      notifyStatus({ status: "disconnected", channel: channelName, wsUrl });
    };

    const handleConnectError = (error: Error) => {
      notifyStatus({
        status: "error",
        channel: channelName,
        wsUrl,
        reason: error?.message ?? "connection_error",
      });
    };

    const handleReconnectAttempt = (attempt: number) => {
      notifyStatus({
        status: "connecting",
        channel: channelName,
        wsUrl,
        retryCount: attempt,
      });
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.io.on("reconnect_attempt", handleReconnectAttempt);
    socket.io.on("error", handleConnectError);
    socket.on("connect_error", handleConnectError);
    socket.on(socketEvent, handleSocketEvent);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.io.off("reconnect_attempt", handleReconnectAttempt);
      socket.io.off("error", handleConnectError);
      socket.off("connect_error", handleConnectError);
      socket.off(socketEvent, handleSocketEvent);
      socket.disconnect();
      if (failTimer) {
        clearTimeout(failTimer);
      }
      if (socketRef.current === socket) {
        socketRef.current = null;
      }
    };
  }, [enabled, socketEvent, userId, wsUrl, channelName]);

  const publishLocal = useCallback(
    (
      payload: Omit<TPayload, "userId" | "origin"> &
        Partial<Pick<TPayload, "userId">>,
    ) => {
      if (!enabled || !userId) return;

      const enrichedPayload: TPayload & RealtimeMessageMeta = {
        ...(payload as TPayload),
        userId: (payload as TPayload)?.userId ?? userId,
        updatedAt: (payload as TPayload)?.updatedAt ?? new Date().toISOString(),
        origin: "local",
        clientId: clientIdRef.current,
      };

      emitMessage(enrichedPayload, "local", clientIdRef.current);

      if (broadcastRef.current) {
        broadcastRef.current.postMessage(enrichedPayload);
      }
    },
    [emitMessage, enabled, userId],
  );

  return {
    publishLocal,
    clientId: clientIdRef.current,
  };
}
